import React from "react";

/**
 * Website blocks (2026-11-01): Try a class, Join the club, Buy credits on the club's own website.
 * The look is stored on the club (users.webWidgets.look, backend config/webWidgets.js has the same defaults),
 * set on the Website & webshop page and read by the public pages through GET shopClub.
 *
 * Embedded mode (?embed=1, set by public/embed.js): no FitClub header, transparent background, the page tells the
 * website its height (postMessage "fitclub:height") so there is no scrollbar inside the block.
 * Preview mode (?preview=1, the Website & webshop page): the page takes unsaved looks / steps from the editor
 * (postMessage "fitclub:preview") and buying is switched off.
 */

export const DEFAULT_LOOK = {
  brandColor: "#481B3A",
  buttonText: "white",
  background: "site",
  backgroundColor: "#FFFFFF",
  corners: "rounded",
  font: "site",
  language: "auto",
  labels: { shop: "", trial: "", join: "" },
  showPerClass: true,
  showValidity: true,
  poweredBy: true,
  productOrder: [],
  hiddenProducts: [],
  highlightProductId: "",
  stripeColor: true,
};

const HEX = /^#[0-9a-f]{6}$/i;

export const mergeLook = (look) => {
  const v = look && typeof look === "object" ? look : {};
  return {
    ...DEFAULT_LOOK,
    ...v,
    brandColor: HEX.test(v.brandColor || "") ? v.brandColor : DEFAULT_LOOK.brandColor,
    backgroundColor: HEX.test(v.backgroundColor || "") ? v.backgroundColor : DEFAULT_LOOK.backgroundColor,
    labels: { ...DEFAULT_LOOK.labels, ...(v.labels || {}) },
    productOrder: Array.isArray(v.productOrder) ? v.productOrder : [],
    hiddenProducts: Array.isArray(v.hiddenProducts) ? v.hiddenProducts : [],
  };
};

/** Corner radius for cards / buttons / fields */
export const RADIUS = {
  square: { card: "6px", button: "4px", field: "4px" },
  rounded: { card: "22px", button: "14px", field: "12px" },
  pill: { card: "28px", button: "9999px", field: "9999px" },
};

const FONTS = {
  mulish: "Mulish, 'Helvetica Neue', Arial, sans-serif",
  inter: "Inter, 'Helvetica Neue', Arial, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
};
const GENERIC = /^(serif|sans-serif|monospace|cursive|fantasy|system-ui|ui-sans-serif|ui-serif|-apple-system|blinkmacsystemfont|inherit|initial)$/i;

/** First font name of the website's font-family (sent by embed.js as ?font=), or "" */
export const cleanFontName = (raw) => {
  const first = String(raw || "").split(",")[0].replace(/["']/g, "").trim();
  if (!first || GENERIC.test(first) || !/^[a-z0-9 \-]{2,40}$/i.test(first)) return "";
  return first;
};

export const fontStack = (look, siteFont) => {
  if (look.font !== "site") return FONTS[look.font] || FONTS.mulish;
  return siteFont ? `'${siteFont}', ${FONTS.mulish}` : FONTS.mulish;
};

/** Loads the website's font from Google Fonts when it is one (harmless otherwise). Once per page. */
export const useGoogleFont = (name) => {
  React.useEffect(() => {
    if (!name || ["Mulish", "Arial", "Helvetica", "Georgia", "Times New Roman", "Verdana"].includes(name)) return;
    const id = `fc-font-${name.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(/%20/g, "+")}:wght@400;600;700;800&display=swap`;
    document.head.appendChild(link);
  }, [name]);
};

/** CSS variables for a block (style={...}) */
export const lookVars = (look, siteFont = "") => {
  const r = RADIUS[look.corners] || RADIUS.rounded;
  return {
    "--fc-brand": look.brandColor,
    "--fc-on-brand": look.buttonText === "black" ? "#111111" : "#FFFFFF",
    "--fc-bg": look.background === "white" ? "#FFFFFF" : look.background === "custom" ? look.backgroundColor : "transparent",
    "--fc-r-card": r.card,
    "--fc-r-btn": r.button,
    "--fc-r-field": r.field,
    fontFamily: fontStack(look, siteFont),
  };
};

/* ------------------------------------------------------------------ words (English / Dutch) */

const WORDS = {
  en: {
    buyCredits: "Buy credits",
    shopIntro: "Pay online with iDEAL, card or Apple Pay. No account needed.",
    buy: "Buy",
    subscribe: "Subscribe",
    mostPopular: "Most popular",
    perClass: (p) => `${p} per class`,
    paidAuto: "Paid automatically",
    validUntilUsed: "Valid until used",
    validDays: (d) => (d === 365 ? "Valid 1 year" : `Valid ${d} days`),
    credits: (n) => `${n} ${n === 1 ? "class" : "classes"}`,
    unlimited: "Unlimited classes",
    creditsEvery: (n, per) => `${n} classes every ${per}`,
    per: { day: "day", week: "week", month: "month", year: "year" },
    perShort: { day: "day", week: "wk", month: "mo", year: "yr" },
    alreadyMember: "Already a member? Use the same mobile number and the credits go straight to your account.",
    noProducts: "Nothing is on sale online right now.",
    back: "Back",
    step: (n, of) => `Step ${n} of ${of}`,
    yourDetails: "Your details",
    detailsWhy: "So we can put the credits on your account.",
    fullName: "Full name",
    mobile: "Mobile number",
    email: "Email",
    emailWhy: "for your receipt",
    codeNote: (club) => `We text you a 4-digit code to confirm it's your number. By continuing you agree to ${club}'s terms.`,
    sendCode: "Send code",
    enterCode: "Enter the code",
    sentTo: (to) => `We sent it to ${to}.`,
    resend: "Send a new code",
    pay: (amount) => `Pay ${amount}`,
    payNote: "You pay on Stripe's secure page. Your bank or card app may ask you to confirm.",
    payNewTab: "The payment page opens in a new tab.",
    paySecurely: "Pay securely",
    payOpened: "Finish paying in the other tab. You can close this window afterwards.",
    payAgain: "Open the payment page again",
    doneTitle: (name) => (name ? `You're all set, ${name}!` : "Payment received"),
    doneText: (product) => `${product ? `${product} is` : "Your purchase is"} on your account. You'll get a receipt by email.`,
    doneNew: "New here? We also emailed you a link to set your password.",
    bookFirst: "Book your first class",
    getApp: "Get the FitClub app",
    backTo: (site) => `Back to ${site}`,
    cancelledTitle: "Payment not finished",
    cancelledText: "Nothing was charged. You can try again whenever you like.",
    tryAgain: "Back to the shop",
    poweredBy: "Powered by",
    previewOff: "Preview: buying is switched off here.",
    closed: "This shop isn't open right now.",
    error: "Something went wrong. Please try again.",
  },
  nl: {
    buyCredits: "Strippenkaarten & abonnementen",
    shopIntro: "Betaal online met iDEAL, kaart of Apple Pay. Geen account nodig.",
    buy: "Kopen",
    subscribe: "Abonneren",
    mostPopular: "Meest gekozen",
    perClass: (p) => `${p} per les`,
    paidAuto: "Automatisch betaald",
    validUntilUsed: "Geldig tot opgebruikt",
    validDays: (d) => (d === 365 ? "1 jaar geldig" : `${d} dagen geldig`),
    credits: (n) => `${n} ${n === 1 ? "les" : "lessen"}`,
    unlimited: "Onbeperkt lessen",
    creditsEvery: (n, per) => `${n} lessen per ${per}`,
    per: { day: "dag", week: "week", month: "maand", year: "jaar" },
    perShort: { day: "dag", week: "wk", month: "mnd", year: "jr" },
    alreadyMember: "Al lid? Gebruik hetzelfde mobiele nummer, dan komen de lessen direct op je account.",
    noProducts: "Er is nu niets online te koop.",
    back: "Terug",
    step: (n, of) => `Stap ${n} van ${of}`,
    yourDetails: "Je gegevens",
    detailsWhy: "Zo zetten we de lessen op je account.",
    fullName: "Volledige naam",
    mobile: "Mobiel nummer",
    email: "E-mail",
    emailWhy: "voor je betaalbewijs",
    codeNote: (club) => `We sms'en je een code van 4 cijfers om je nummer te bevestigen. Door verder te gaan ga je akkoord met de voorwaarden van ${club}.`,
    sendCode: "Stuur code",
    enterCode: "Vul de code in",
    sentTo: (to) => `We hebben hem gestuurd naar ${to}.`,
    resend: "Stuur een nieuwe code",
    pay: (amount) => `Betaal ${amount}`,
    payNote: "Je betaalt op de beveiligde pagina van Stripe. Je bank- of kaart-app kan om bevestiging vragen.",
    payNewTab: "De betaalpagina opent in een nieuw tabblad.",
    paySecurely: "Veilig betalen",
    payOpened: "Rond de betaling af in het andere tabblad. Daarna kun je dit venster sluiten.",
    payAgain: "Open de betaalpagina opnieuw",
    doneTitle: (name) => (name ? `Klaar, ${name}!` : "Betaling ontvangen"),
    doneText: (product) => `${product ? `${product} staat` : "Je aankoop staat"} op je account. Je krijgt een betaalbewijs per e-mail.`,
    doneNew: "Nieuw? We hebben je ook een link gemaild om je wachtwoord te kiezen.",
    bookFirst: "Boek je eerste les",
    getApp: "Download de FitClub-app",
    backTo: (site) => `Terug naar ${site}`,
    cancelledTitle: "Betaling niet afgerond",
    cancelledText: "Er is niets afgeschreven. Je kunt het opnieuw proberen wanneer je wilt.",
    tryAgain: "Terug naar de shop",
    poweredBy: "Mogelijk gemaakt door",
    previewOff: "Voorbeeld: kopen staat hier uit.",
    closed: "Deze shop is nu niet open.",
    error: "Er ging iets mis. Probeer het opnieuw.",
  },
};

export const pickLang = (look, override = "") => {
  const want = override || look?.language || "auto";
  if (want === "en" || want === "nl") return want;
  try {
    const nav = (navigator.languages && navigator.languages[0]) || navigator.language || "";
    return /^nl/i.test(nav) ? "nl" : "en";
  } catch (e) {
    return "en";
  }
};
export const wordsFor = (lang) => WORDS[lang] || WORDS.en;

/* ------------------------------------------------------------------ embedded / preview mode */

const EMBED_KEY = "fc:embed";

const params = () => {
  try {
    return new URLSearchParams(window.location.search);
  } catch (e) {
    return new URLSearchParams();
  }
};

/**
 * { embed, preview, siteFont, lang, previewMessage } for a public page. In embedded mode the page background is
 * transparent and its height is sent to the website (embed.js resizes the block).
 */
export const useEmbedMode = () => {
  const [state] = React.useState(() => {
    const p = params();
    const fromUrl = {
      embed: p.get("embed") === "1",
      preview: p.get("preview") === "1",
      siteFont: cleanFontName(p.get("font") || ""),
      lang: ["en", "nl"].includes(p.get("lang")) ? p.get("lang") : "",
      id: (p.get("fcid") || "").slice(0, 40),
    };
    // Pages inside the block navigate (e.g. /trial -> /trial/book/…) and lose the query: remember it for this frame
    let inFrame = false;
    try {
      inFrame = window.self !== window.top;
    } catch (e) {
      inFrame = true;
    }
    try {
      if (fromUrl.embed || fromUrl.preview) sessionStorage.setItem(EMBED_KEY, JSON.stringify(fromUrl));
      else if (inFrame) {
        const saved = JSON.parse(sessionStorage.getItem(EMBED_KEY) || "null");
        if (saved && (saved.embed || saved.preview)) return { ...saved, siteFont: cleanFontName(saved.siteFont || "") };
      }
    } catch (e) {
      /* storage blocked: only the first page knows it is embedded */
    }
    return fromUrl;
  });
  const [previewMessage, setPreviewMessage] = React.useState(null);

  React.useEffect(() => {
    if (!state.embed && !state.preview) return undefined;
    const prev = { html: document.documentElement.style.background, body: document.body.style.background };
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    let last = 0;
    const send = () => {
      // the content's own height (the document is never smaller than the frame, so it can't be used to shrink)
      const root = document.getElementById("root") || document.body;
      const h = Math.ceil(root.getBoundingClientRect().height) + 2;
      if (!h || h === last || window.parent === window) return;
      last = h;
      window.parent.postMessage({ type: "fitclub:height", height: h, id: state.id }, "*");
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(send) : null;
    if (ro) ro.observe(document.getElementById("root") || document.body);
    const t = setInterval(send, 1000);
    send();
    return () => {
      if (ro) ro.disconnect();
      clearInterval(t);
      document.documentElement.style.background = prev.html;
      document.body.style.background = prev.body;
    };
  }, [state]);

  React.useEffect(() => {
    if (!state.preview) return undefined;
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return; // only the FitClub editor itself
      if (e.data && e.data.type === "fitclub:preview") setPreviewMessage(e.data);
    };
    window.addEventListener("message", onMessage);
    if (window.parent !== window) window.parent.postMessage({ type: "fitclub:preview-ready" }, window.location.origin);
    return () => window.removeEventListener("message", onMessage);
  }, [state]);

  return { ...state, previewMessage };
};

/** Where a block lives: the club's own address (https://<club>.<root>/), or this site + ?club= for local testing */
export const clubSiteBase = (myBookingLink) => {
  const host = window.location.hostname;
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return `${window.location.origin}/`;
  const parts = host.split(".");
  const root = parts.length > 2 ? parts.slice(-2).join(".") : host;
  return myBookingLink ? `https://${myBookingLink}.${root}/` : `https://${root}/`;
};
